<script lang="ts" setup>
    import {computed, onMounted,watch,ref,reactive} from 'vue';
    import {getScreenInfo} from '../../lib/ChoiceUserConfiguration';
    import {AppScreens,PrivilegeType,Alignment} from '../../Enums/ChoiceEnums';
    import {ButtonDisplaySize,ButtonDisplayStyle,ModalSize} from '../../Enums/UiEnums';
    import ModalDialog from "../../components/ModalDialog.vue"
    import Validator from '../../components/Validator.vue';
    import CurrencyInput from '../../components/CurrencyInput.vue';
    import IntegerInput from '../../components/IntegerInput.vue';
    import {paymentbatch,PaymentBatchUpdateType} from '../../lib/Models/BillingModel'; 
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import TextInput from '../../components/TextInput.vue';
    import {NoTzDate ,getNoTzToday} from "../../lib/DateUtils"
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import {ActionButtonStyle,billingBaseClass} from '../../lib/Types/Types';
      
    const screenSecurityLevel = ref<PrivilegeType>(); 
    const validateSection = ref();  
    const formData = reactive <
    {   
        resultsError: {flag: boolean, message: string}, 
        resultsWarning: {flag: boolean, message: string},  
        inputs: {payment_batch_date?: NoTzDate, total_input_value?: number, item_input_value?: number, notes?: string},   
        newPaymentBatchID: number 
    }>
    ({     
        resultsError: {flag: false, message: ""}, 
        resultsWarning: {flag: false, message: ""},  
        inputs: {payment_batch_date: getNoTzToday(), total_input_value: 0.00, item_input_value: 0, notes: ""},  
        newPaymentBatchID: 0
    });
    
    onMounted(async () => {
        const screenJson = await getScreenInfo(AppScreens.NewPaymentBatch); 
        screenSecurityLevel.value = screenJson?.security ?? PrivilegeType.None;
    });

    watch(() => props.clearDialog, (newValue : boolean, oldValue : boolean) => { 
        if (newValue) {
            formData.inputs = {payment_batch_date: getNoTzToday(), total_input_value: 0.00, item_input_value: 0, notes: ""};
        }
    });

    const disableSave = computed(()=>  {
        if (!screenSecurityLevel.value) {
            return false; 
        }
        return (screenSecurityLevel.value < PrivilegeType.ReadUpdate);
    });
    const enableInput = computed(()=> {
        if (!screenSecurityLevel.value) {
            return false; 
        }
        return (screenSecurityLevel.value > PrivilegeType.None);
    });

    const props = withDefaults( defineProps<{
            showDialog: boolean,
            clearDialog?: boolean
        }>(),
        {
            showDialog: false,
            clearDialog: false
        });

    const emit=defineEmits(["close-vue-modal-iframe"]);  

    // functions
    async function CloseDialog() : Promise<void> {  
        emit('close-vue-modal-iframe',formData.newPaymentBatchID); 
    } 
    async function savePaymentBatch()  : Promise<void>  { 
        let total_amount = +(formData.inputs.total_input_value ?? 0);
        let total_items = +(formData.inputs.item_input_value ?? 0);
      
        const paymentBatchInputs = {update_command: PaymentBatchUpdateType.Insert, total_amount: total_amount, item_count: total_items, payment_batch_date: formData.inputs.payment_batch_date, description: formData.inputs.notes};
        const respSave = await paymentbatch.UpdatePaymentBatch(paymentBatchInputs);  
    
        if (respSave.error) {
            formData.resultsError = {flag: true, message: (respSave.error.error ?? "Unable to perform request")};
            return;
        }
        if (respSave.success) { 
            formData.resultsError = {flag: (respSave.success.errorCode === 0 ? false : true), message: respSave.success.errorMessage ?? "Unable to perform request"} ; 
            
            if (!formData.resultsError.flag) {
                formData.newPaymentBatchID = respSave.success.paymentBatchId ?? 0;
                CloseDialog();
            }
        }
    } 
    function ResetErrorMessage() : void {
        formData.resultsError = {flag: false, message: ""};
        formData.resultsWarning = {flag: false, message: ""};
    }  
    function ValidateFutureDate() : void { 
        ResetErrorMessage();
        const today = new Date(getNoTzToday()); 
        const new_date = (formData.inputs.payment_batch_date ? new Date(formData.inputs.payment_batch_date) : today);
        if (new_date > today) { 
            formData.resultsWarning = {flag: true, message: "Warning: Batch Date is in the future!"};
        }
    }  
</script>

<style scoped>
    .fixedPage {
        width: 460px;
        font-family: Arial, Helvetica, sans-serif; 
        margin: 5px 5px 5px 5px;
        background-color: white;
    }
    .viewDisplay {
        display: block;
    }
    .viewHide { 
        display: none;
    }
    .float-right {
        text-align: right;
    }
    .label_noborder {
        border: 1px solid transparent;
    }
    .label_noborder_gray {
        border: 1px solid transparent;
        color: silver;        
	    FONT-STYLE: italic;
    }
    ::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
        color: SILVER;
	    FONT-STYLE: italic;
        text-align: left;
        opacity: 1; /* Firefox */
     }
     ::-webkit-input-placeholder {
        color: SILVER;
	    FONT-STYLE: italic;
        text-align: left;
     }
</style>

<template> 
    <ModalDialog :size=ModalSize.Medium :show="props.showDialog" :closeOnClickOutside="false" @hidden="CloseDialog">
        <template v-slot:title>New Payment Batch</template>
        <template #footer>
            <div v-if="formData.resultsWarning.flag"  class="container-fluid text-warning">
                {{formData.resultsWarning.message}}
            </div>    
            <div v-if="formData.resultsError.flag"  class="container-fluid text-danger">
                {{formData.resultsError.message}}
            </div>    
            <div  class="container-fluid">
                <Validator ref="validateSection">
                    <div v-show="!formData.resultsError.flag" class="row pb-1">
                        <div class="col-6"></div>
                        <div class="col-6 text-end">
                            
                            <ActionButtonV2 
                                :action="ActionButtonStyle.Save"
                                :disabled="disableSave"   
                                :text="'Save'"
                                :spinText="'Saving'"
                                :toolTip="'Save new Payment Batch'"
                                :size="ButtonDisplaySize.Small" 
                                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                                :performAction="savePaymentBatch"
                                :endingPadding="1"/> 
                            <ActionButtonV2 
                                :action="ActionButtonStyle.Close"
                                :text="'Close'"
                                :size="ButtonDisplaySize.Small" 
                                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                                :performAction="CloseDialog"/> 
                        </div>
                    </div>
                </Validator>
            </div>
        </template>
        <div v-if="enableInput" class="container-fluid">
            <div class="row">
                <div class="fixedPage"> 
                    <div class="row pb-1">
                        <div class="col-4">
                            <span class="label_noborder form-control">Status</span> 
                        </div>
                        <div class="col-8">
                            <span class="label_noborder_gray form-control">New</span> 
                        </div>
                    </div>  
                    <div class="row pb-1">
                        <div class="col-4">
                            <span class="label_noborder form-control">Batch Date</span> 
                        </div>
                        <div class="col-8">
                            <DatePickerNoTZ
                                v-model:value="formData.inputs.payment_batch_date" 
                                :clearable="false" 
                                :required=true
                                :errorDisplay=false 
                                :placeholder="'Payment Date'"
                                @update:value="()=>ValidateFutureDate()"
                                @validationState="(state, msg) => validateSection?.sinkValidation('Batch Date', state, msg)"  
                            /> 
                        </div>
                    </div>  
                    <div class="row pb-1">
                        <div class="col-4">
                            <span class="label_noborder form-control">Total</span> 
                        </div>
                        <div class="col-8">
                            <CurrencyInput 
                                v-model:numberValue="formData.inputs.total_input_value" 
                                :maxLength=12 
                                :startingRange=0.00
                                :endingRange=1000000.00
                                :positive=true 
                                :alignment=Alignment.left
                                placeholder="Total amount" 
                                @update:numberValue="()=>ResetErrorMessage()"
                                @validationState="(state, msg) => validateSection?.sinkValidation('Total', state, msg)"  
                            />   
                        </div>
                    </div>  
                    <div class="row pb-1">
                        <div class="col-4">
                            <span class="label_noborder form-control">Items</span> 
                        </div>
                        <div class="col-8"> 
                            <IntegerInput 
                                v-model:numberValue="formData.inputs.item_input_value" 
                                :maxLength=6 
                                :startingRange=0
                                :endingRange=99999
                                :positive=true
                                :alignment=Alignment.left
                                placeholder="Total items"
                                LeftAddon="fa-solid fa-hashtag"
                                @update:numberValue="()=>ResetErrorMessage()" 
                                @validationState="(state, msg) => validateSection?.sinkValidation('Items', state, msg)"  
                            />   
                        </div>
                    </div>
                    <div class="row pb-1">
                        <div class="col-4">
                            <span class="label_noborder form-control">Notes</span> 
                        </div>
                        <div class="col-8">
                            <TextInput 
                                :rows=3
                                v-model:textValue="formData.inputs.notes" 
                                placeholder="Payment Batch notes"
                                @update:text-value="ResetErrorMessage()" 
                            />
                        </div>
                    </div>
                </div> 
            </div> 
        </div>
        <div v-else class="container-fluid">
            Your user rights do not allow you to view this page.
        </div>
    </ModalDialog>
</template>

